import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./styles/ScenarioCardForum.module.css";

import ScenarioCardTagsRow from "./ScenarioCardTagsRow";
import { FaEye, FaStar, FaRegStar, FaRegComment, FaBookmark, FaRegBookmark } from "react-icons/fa";
import ProfileIcon from '../../../images/profileicon.png';
import * as SavedScenarioAPI from "../../../services/SavedScenarioService";

const ScenarioCardForum = ({
    scenario,
    isHovered,
    setShowShareModal,
    setHovered,
    searchQuery,
    highlightText,
    handleTagClick,
    onSavedChange,

}) => {
    const navigate = useNavigate();
    const [width, setWidth] = useState(window.innerWidth);
    
    // SAVING SCENARIO //
    const [isSaved, setIsSaved] = useState(Boolean(scenario.isSaved));
    const [isUpdatingSaved, setIsUpdatingSaved] = useState(false);
    const [savedCounts, setSavedCounts] = useState(scenario.savedCounts ?? 0 );
    

    // Track window resize for responsive star rendering
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleToggleSave = async (event) => {
        event.stopPropagation();

        if (isUpdatingSaved) {
            return;
        }

        try {
            setIsUpdatingSaved(true);

            if (isSaved) {
                const response =
                    await SavedScenarioAPI.unsaveScenario(
                        scenario._id
                    );

                setIsSaved(false);
                setSavedCounts(response.savedCounts ?? 0);

                onSavedChange?.(
                    scenario._id,
                    false,
                    response.savedCounts ?? 0
                );
            } else {
                const response =
                    await SavedScenarioAPI.saveScenario(
                        scenario._id
                    );

                setIsSaved(true);
                setSavedCounts(response.savedCounts ?? 0);

                onSavedChange?.(
                    scenario._id,
                    true,
                    response.savedCounts ?? 0
                );
            }
        } catch (err) {
            console.error(
                "Error toggling saved scenario:",
                err.response?.data || err
            );
        } finally {
            setIsUpdatingSaved(false);
        }
    };

    useEffect(() => {
        setIsSaved(Boolean(scenario.isSaved));
        setSavedCounts(scenario.savedCounts ?? 0);
    }, [scenario._id, scenario.isSaved, scenario.savedCounts]);

    const handleTitleClick = () => {
        if (scenario.isPublished) navigate(`/about/${scenario._id}`);
    };

    const handleAuthorClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/user/${scenario.creator?.username}`);
    };

    // Safe fallbacks for legacy data
    const avgRating = scenario.avgRating || 0;
    const ratingsCount = scenario.ratingsCount || 0;
    const commentsCount = scenario.commentsCount || 0;
    const viewsCount = scenario.views || 0;

    const creator = scenario.creator;
    const creatorProfilePicture = typeof creator === 'object' ? creator.profilePicture : null;

    // Logic for rendering 5 stars with partial fills (desktop)
    const renderStarsTenths = (rating, max = 5) => {
        const stars = [];
        for (let i = 1; i <= max; i++) {
            const fill = Math.min(Math.max(rating - (i - 1), 0), 1);
            if (fill >= 1) {
                stars.push(<FaStar key={i} style={{ color: "#f5c518", marginRight: "2px" }} />);
            } else if (fill > 0) {
                stars.push(
                    <span key={i} style={{ display: "inline-block", position: "relative", width: "1em", marginRight: "2px" }}>
                        <FaStar style={{ position: "absolute", left: 0, top: 0, width: "1em", clipPath: `inset(0 ${100 - fill * 100}% 0 0)`, color: "#f5c518" }} />
                        <FaRegStar style={{ color: "#ddd" }} />
                    </span>
                );
            } else {
                stars.push(<FaRegStar key={i} style={{ color: "#ddd", marginRight: "2px" }} />);
            }
        }
        return stars;
    };

    // Logic for rendering a single star with partial fill (mobile)
    const renderSingleStarFifths = (rating) => {
        const filled = Math.ceil((rating / 5) * 5) / 5; 
        return (
            <span style={{ display: "inline-block", position: "relative", width: "1em", marginRight: "4px" }}>
                <FaStar style={{ position: "absolute", top: 0, left: 0, width: "1em", clipPath: `inset(0 ${100 - filled * 100}% 0 0)`, color: "#f5c518" }} />
                <FaRegStar style={{ color: "#ddd" }} />
            </span>
        );
    };

    return (
        <div className={styles.card}>
            <div className={styles.contentGrid}>
                {/* Title */}
                <div
                    className={`${styles.title} ${isHovered ? styles.hovered : ""}`}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onClick={handleTitleClick}
                    title={scenario.title}
                >
                    {scenario.title || "Untitled"}
                </div>

            {/* Save Button */}
            <div className={styles.saveButton}>
                <span className={styles.savedCount}>
                    {savedCounts}
                </span>

                <button
                    type="button"
                    className={styles.iconButton}
                    onClick={handleToggleSave}
                    disabled={isUpdatingSaved}
                    aria-label={isSaved ? "Unsave scenario" : "Save scenario"}
                    aria-pressed={isSaved}
                    title={
                        isUpdatingSaved
                            ? "Updating..."
                            : isSaved
                                ? "Unsave Scenario"
                                : "Save Scenario"
                    }
                >
                    {isSaved ? (
                        <FaBookmark className={styles.savedBookmark} />
                    ) : (
                        <FaRegBookmark className={styles.unsavedBookmark} />
                    )}
                </button>
            </div>
                {/* Tags Row */}
                <ScenarioCardTagsRow
                    tags={scenario.tags}
                    searchQuery={searchQuery}
                    highlightText={highlightText}
                    handleTagClick={handleTagClick}
                />

                {/* Author Info */}
                <div className={styles.author}>
                    <div className={styles.profilePictureContainer}>
                        <img 
                            className={styles.profilePictureImage} 
                            src={creatorProfilePicture || ProfileIcon} 
                            alt="Profile" 
                        />
                    </div>
                    <a href={`/user/${creator?.username}`} onClick={handleAuthorClick} className={styles.usernameLink}>
                        {creator?.username || "Unknown Author"}
                    </a>
                </div>

                {/* Stats Section */}
                <div className={styles.stats}>
                    {/* Dynamic Rating Display */}
                    <div className={styles.statItem} title={`Rating: ${avgRating} based on ${ratingsCount} ratings`}>
                        {width < 1024 ? renderSingleStarFifths(avgRating) : renderStarsTenths(avgRating)}
                        <span className={styles.statCount}>
                            {avgRating > 0 ? avgRating.toFixed(1) : "No ratings"}
                            {ratingsCount > 0 && <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.8 }}>({ratingsCount})</span>}
                        </span>
                    </div>

                    {/* Views Stat */}
                    <div className={styles.statItem} title="Views">
                        <FaEye className={styles.viewsIcon} />
                        <span className={styles.statCount}>{viewsCount}</span>
                    </div>

                    {/* Accurate Comments Stat (Denormalized) */}
                    <div className={styles.statItem} title="Comments">
                        <FaRegComment className={styles.commentsIcon} />
                        <span className={styles.statCount}>{commentsCount}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScenarioCardForum;